import * as icons from '@mui/icons-material';
import type { PaperProps } from '@mui/material';
import { Autocomplete, Box, Paper, Stack, TextField } from '@mui/material';
import { useAutocomplete } from '@refinedev/mui';
import type { PropsWithChildren } from 'react';
import { forwardRef, useMemo } from 'react';
import { Controller, useFieldArray, useFormContext, type RegisterOptions } from 'react-hook-form';
import type { DocumentHead, MagazineAnnouncement } from '../../types/index.ts';
import { SortableList } from '../SortableList.tsx';
import { ReadOnlyField } from './ReadOnlyField.tsx';

type SortableListFieldProps = {
  name: string;
  rules?: RegisterOptions;
  label: string;
};

export const MagazineAnnouncementField = forwardRef<HTMLDivElement, SortableListFieldProps>(
  ({ name, label, rules, ...props }, ref) => {
    const { control } = useFormContext<{
      magazineAnnouncements: Array<Partial<MagazineAnnouncement>>;
    }>();
    const { fields, append, remove, replace, update } = useFieldArray({
      control,
      name: 'magazineAnnouncements',
    });

    const { autocompleteProps } = useAutocomplete<DocumentHead>({
      resource: 'contents/documents',
      filters: [{ field: 'type', operator: 'eq', value: 'magazine_toc' }],
      onSearch: (value) => [{ field: 'title', operator: 'contains', value }],
    });

    const fieldMap = useMemo(() => new Map(fields.map((field) => [field.id, field])), [fields]);

    return (
      <ReadOnlyField
        label={label}
        margin="normal"
        variant="outlined"
        helperText="順番をドラッグ＆ドロップで入れ替えることができます"
        focused={false}
      >
        <Box sx={{ gap: 1, display: 'flex', flexDirection: 'column' }}>
          <SortableList
            sortStrategy="verticalList"
            ids={fields.map((field) => field.id) ?? []}
            onChange={(ids) => {
              const fields = ids.map((id) => fieldMap.get(id as string)!);
              replace(fields);
            }}
            renderItem={(id, index) => {
              return (
                <MagazineTocDocumentCard
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <icons.DragHandleOutlined sx={{ cursor: 'grab' }} />
                  <Stack spacing={1} sx={{ width: '100%' }} onClick={(e) => e.stopPropagation()}>
                    <Controller
                      name={`magazineAnnouncements.${index}.magazineTocDocument`}
                      control={control}
                      rules={{ required: '雑誌目次は必須です' }}
                      render={({ field, fieldState: { error } }) => (
                        <Autocomplete
                          {...field}
                          {...autocompleteProps}
                          size="small"
                          onChange={(_, value) => {
                            update(index, {
                              ...fields[index],
                              magazineTocDocument: value!,
                              text: value!.title,
                            });
                          }}
                          filterSelectedOptions
                          getOptionKey={(option) => option.id}
                          getOptionLabel={(option) => option.title}
                          fullWidth
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="雑誌目次"
                              variant="outlined"
                              fullWidth
                              error={!!error}
                              helperText={error?.message}
                            />
                          )}
                        />
                      )}
                    />
                    <Controller
                      name={`magazineAnnouncements.${index}.text`}
                      control={control}
                      rules={{
                        required: 'アナウンス文は必須です',
                      }}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          size="small"
                          label="アナウンス文"
                          variant="outlined"
                          fullWidth
                          {...field}
                          error={!!error}
                          helperText={
                            error?.message ??
                            'タイトルが自動でアナウンス文に入ります。必要に応じて編集してください。'
                          }
                        />
                      )}
                    />
                  </Stack>
                  <icons.DeleteOutlineOutlined
                    sx={{ cursor: 'pointer', color: 'error.main' }}
                    onClick={() => remove(index)}
                  />
                </MagazineTocDocumentCard>
              );
            }}
          />
        </Box>
        {fields.length < 3 && (
          <icons.AddOutlined
            sx={{
              cursor: 'pointer',
              marginTop: 1,
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              alignSelf: 'center',
              justifyContent: 'center',
              width: '100%',
            }}
            onClick={() => append({})}
          />
        )}
      </ReadOnlyField>
    );
  },
);

const MagazineTocDocumentCard = ({ children, ...props }: PropsWithChildren<PaperProps>) => {
  return (
    <Paper
      {...props}
      variant="outlined"
      sx={{
        ...props.sx,
        gap: 1,
        p: 2,
      }}
    >
      {children}
    </Paper>
  );
};
